import { CheckCircleFilled } from "@ant-design/icons";
import { Drawer, Skeleton, Spin } from "antd";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled, { createGlobalStyle } from "styled-components";
import { StyledFormTableInput } from "../../../../../components/atoms/Input/Input.styles";
import { PortalHeader } from "../../../../../components/molecules/Header";
import { portalDrawerAntHeaderCss } from "../../../../../components/molecules/Header/Header.styles";
import { useNotification } from "../../../../../hooks/useNotification";
import {
  useConsignmentToReceiptMutation,
  useCreateReceiptsFromPOMutation,
} from "../../../../../store/apiSlice/receiptApiSlice";
import { purchaseOrderApiSlice } from "../../../../../store/apiSlice/purchaseOrderApiSlice";
import { shipmentApiSlice } from "../../../../../store/apiSlice/shipmentApiSlice";
import { getAllocationApiErrorMessage } from "../../Allocation/action/createShipmentHelpers";
import { Button } from "../../components/Button/Button";
import {
  buildConsignmentToReceiptPayload,
  buildReceiptPreviewFromConsignments,
  effectiveReceiveQty,
  getEditedReceiveQtyText,
  initialCheckedReceiptLineIds,
  validateReceiveLineQty,
} from "./createReceiptFromShipmentHelpers";
import {
  buildPurchaseOrderToReceiptPayload,
  buildReceiptPreviewFromPurchaseOrder,
  initialCheckedReceiptLineIds as initialCheckedPurchaseReceiptLineIds,
  receiptFromPurchaseOrderResponse,
} from "./createReceiptFromPurchaseHelpers";

const PREVIEW_ROW_HEIGHT = 40;
const PREVIEW_OVERSCAN = 6;

const PreviewLines = ({
  allChecked,
  checkedLineIds,
  lineQuantities,
  lines,
  onQtyChange,
  qtyTabOrder,
  someChecked,
  toggleGroup,
  toggleLine,
  viewportHeight = 0,
  groupLabel,
}) => {
  const [start, setStart] = useState(0);
  const scrollRef = useRef(null);
  const useWindow =
    viewportHeight > 0 && lines.length * PREVIEW_ROW_HEIGHT > viewportHeight;
  const visibleCount =
    Math.ceil(viewportHeight / PREVIEW_ROW_HEIGHT) + PREVIEW_OVERSCAN * 2;
  const rowStart = useWindow ? Math.min(start, Math.max(0, lines.length - 1)) : 0;
  const rowEnd = useWindow ? Math.min(lines.length, rowStart + visibleCount) : lines.length;

  const focusQtyInput = useCallback(
    lineId => {
      const lineIndex = lines.findIndex(line => line.line_id === lineId);
      const inThisTable = lineIndex >= 0;
      const selector = `.shipment-create-receipt-drawer [data-receipt-qty-line="${lineId}"]`;

      const focusElement = () => {
        const el = document.querySelector(selector);
        if (!el) {
          return;
        }
        if (!inThisTable || !useWindow) {
          el.scrollIntoView({ block: "nearest" });
        }
        el.focus();
      };

      if (inThisTable && useWindow && scrollRef.current) {
        scrollRef.current.scrollTop = lineIndex * PREVIEW_ROW_HEIGHT;
        requestAnimationFrame(() => {
          requestAnimationFrame(focusElement);
        });
        return;
      }

      requestAnimationFrame(focusElement);
    },
    [lines, useWindow],
  );

  const handleQtyKeyDown = (event, lineId) => {
    if (event.key !== "Tab") {
      return;
    }

    const currentIndex = qtyTabOrder.indexOf(lineId);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= qtyTabOrder.length) {
      return;
    }

    event.preventDefault();
    focusQtyInput(qtyTabOrder[nextIndex]);
  };

  const onScroll = event => {
    const next = Math.max(
      0,
      Math.floor(event.currentTarget.scrollTop / PREVIEW_ROW_HEIGHT) - PREVIEW_OVERSCAN,
    );
    setStart(current => (current === next ? current : next));
  };

  return (
    <div
      ref={scrollRef}
      onScroll={useWindow ? onScroll : undefined}
      style={
        useWindow
          ? { maxHeight: viewportHeight, overflowY: "auto", minHeight: 0 }
          : undefined
      }
    >
      <LineTable>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={allChecked}
                ref={input => {
                  if (input) {
                    input.indeterminate = someChecked && !allChecked;
                  }
                }}
                onChange={event => toggleGroup(event.target.checked)}
                aria-label={`Select all lines for ${groupLabel}`}
              />
            </th>
            <th>#</th>
            <th>Order</th>
            <th>Product</th>
            <th>Dispatched</th>
            <th>Received</th>
            <th>Receive qty</th>
          </tr>
        </thead>
        <tbody>
          {useWindow && rowStart > 0 && (
            <tr aria-hidden="true">
              <td colSpan={7} style={{ height: rowStart * PREVIEW_ROW_HEIGHT, padding: 0 }} />
            </tr>
          )}
          {lines.slice(rowStart, rowEnd).map((line, sliceIndex) => {
            const qtyText = getEditedReceiveQtyText(lineQuantities, line.line_id, line);
            const isChecked = checkedLineIds.has(line.line_id);
            const qtyValid = !isChecked || validateReceiveLineQty(qtyText).valid;

            return (
              <tr
                key={line.line_id}
                style={useWindow ? { height: PREVIEW_ROW_HEIGHT } : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleLine(line.line_id)}
                    aria-label={`Include ${line.order_number} ${line.product_number}`}
                  />
                </td>
                <td>
                  {line.line_number ??
                    line.line_no ??
                    (useWindow ? rowStart + sliceIndex + 1 : sliceIndex + 1)}
                </td>
                <td>{line.order_number || "—"}</td>
                <td>
                  {line.product_number}
                  {line.product_name ? ` · ${line.product_name}` : ""}
                </td>
                <td>{line.dispatched_qty}</td>
                <td>{line.received_qty}</td>
                <td>
                  <QtyInput
                    type="text"
                    inputMode="numeric"
                    data-receipt-qty-line={line.line_id}
                    aria-label={`Receive quantity for ${line.product_number}`}
                    aria-invalid={!qtyValid}
                    disabled={!isChecked}
                    tabIndex={isChecked ? 0 : -1}
                    value={qtyText}
                    $invalid={!qtyValid}
                    onChange={event => onQtyChange(line.line_id, event.target.value)}
                    onKeyDown={event => handleQtyKeyDown(event, line.line_id)}
                  />
                </td>
              </tr>
            );
          })}
          {useWindow && lines.length > rowEnd && (
            <tr aria-hidden="true">
              <td
                colSpan={7}
                style={{ height: (lines.length - rowEnd) * PREVIEW_ROW_HEIGHT, padding: 0 }}
              />
            </tr>
          )}
        </tbody>
      </LineTable>
    </div>
  );
};

const DrawerTheme = createGlobalStyle`
  .shipment-create-receipt-drawer .ant-drawer-content {
    background: #fff;
    border-left: 1px solid var(--PEARL, #ecf1f4);
    color: var(--GREY, #4a4a68);
    font-family: Inter, sans-serif;
  }
  .shipment-create-receipt-drawer .ant-drawer-header {
    ${portalDrawerAntHeaderCss}
  }
  .shipment-create-receipt-drawer .ant-drawer-title {
    font-family: inherit;
    width: 100%;
  }
  .shipment-create-receipt-drawer .ant-drawer-body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const Scroll = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px 24px;
  min-height: 0;
`;

const ReviewMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0 24px 20px;
`;

const SummaryBar = styled.div`
  padding: 14px 24px;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  background: #f8fafb;
  font-size: 14px;
  line-height: 1.5;
  flex-shrink: 0;
`;

const FullyReceiptedNote = styled.div`
  flex-shrink: 0;
  padding: 12px 24px;
  border-bottom: 1px solid #f3d9a4;
  background: #fff8e8;
  color: #4a4a68;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
`;

const ShipmentCard = styled.section`
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  display: ${({ $fill }) => ($fill ? "flex" : "block")};
  flex: ${({ $fill }) => ($fill ? "1" : "none")};
  flex-direction: column;
  min-height: 0;
`;

const ShipmentHeader = styled.div`
  padding: 12px 16px;
  background: #f3f6f8;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`;

const LinesViewport = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const LineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 12px;
    border-top: 1px solid var(--PEARL, #ecf1f4);
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 600;
    color: #6b7280;
    background: #fff;
  }

  td:first-child,
  th:first-child {
    width: 40px;
    text-align: center;
  }

  td:nth-child(6) {
    width: 96px;
  }
`;

const QtyInput = styled(StyledFormTableInput)`
  border-color: ${({ $invalid }) => ($invalid ? "#cf324a" : undefined)};
  height: 32px;
  padding: 4px 8px;
`;

const SkippedSection = styled.div`
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px dashed var(--PEARL, #ecf1f4);
  flex-shrink: 0;

  h4 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: #6b7280;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--PEARL, #ecf1f4);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fff;
  flex-shrink: 0;
`;

const ErrorBlock = styled.div`
  padding: 24px;
  text-align: center;

  p {
    margin-bottom: 16px;
    color: #4a4a68;
  }
`;

const SuccessColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SuccessContentCenter = styled.div`
  max-width: 520px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SuccessHero = styled.div`
  text-align: center;
  padding: 8px 0 4px;
`;

const SuccessIconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: rgba(37, 135, 80, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: var(--SUCCESS, #258750);
`;

const SuccessHeading = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--GREY, #4a4a68);
`;

const SuccessSubtext = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;

  a {
    color: var(--DARK-BLUE, #1249bf);
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const FailureCallout = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid rgba(207, 50, 74, 0.35);
  background: rgba(207, 50, 74, 0.06);
  font-size: 13px;
  line-height: 1.5;
  color: var(--GREY, #4a4a68);

  h3 {
    margin: 0 0 10px;
    font-size: 13px;
    font-weight: 600;
    color: #cf324a;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  li {
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SuccessTableWrap = styled.div`
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 8px;
  overflow: hidden;
`;

const SuccessTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  line-height: 1.5;

  th,
  td {
    padding: 12px 16px;
    border-top: 1px solid var(--PEARL, #ecf1f4);
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    border-top: none;
    font-weight: 600;
    font-size: 13px;
    color: #6b7280;
    background: #f3f6f8;
  }

  tbody tr:first-child td {
    border-top: none;
  }

  td {
    color: var(--GREY, #4a4a68);
    font-weight: 500;
  }

  a {
    color: var(--DARK-BLUE, #1249bf);
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const buildLinesById = groups => {
  const map = {};
  (groups || []).forEach(group => {
    (group.lines || []).forEach(line => {
      map[line.line_id] = line;
    });
  });
  return map;
};

const countGroupsWithLines = groups =>
  (groups || []).filter(group => (group.lines || []).length > 0).length;

const receiptFromConsignmentResponse = response => {
  const lines = Array.isArray(response) ? response : [];
  if (!lines.length) {
    return { receiptId: undefined, receiptName: undefined };
  }
  const receiptId = lines.find(line => line.receipt_id != null)?.receipt_id ?? lines[0].receipt_id;
  const receiptName =
    lines.find(line => line.receipt_name)?.receipt_name ?? lines[0].receipt_name;
  return { receiptId, receiptName };
};

const formatReceiptNumber = (receiptName, receiptId) => {
  if (receiptName) {
    return receiptName;
  }
  if (receiptId != null) {
    return `#${receiptId}`;
  }
  return null;
};

/**
 * Review drawer: receive consignment lines into receipts (one API call per shipment).
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: function(): void,
 *   source: "list"|"detail",
 *   shipments?: Array<object>,
 *   detailConsignment?: { id: number, name?: string, reference?: string, items?: Array<object> },
 *   detailPurchaseOrder?: { id: number, name?: string, status?: string, items?: Array<object> },
 *   receiptDocument?: "shipment"|"purchase",
 *   preselectedLineIds?: number[],
 *   setSelectedOrder?: function(Array): void,
 *   onSuccess?: function(): void,
 * }} props
 */
export const CreateReceiptReviewDrawer = ({
  isOpen,
  onClose,
  source = "list",
  shipments = [],
  detailConsignment,
  detailPurchaseOrder,
  receiptDocument = "shipment",
  preselectedLineIds = [],
  setSelectedOrder,
  onSuccess,
}) => {
  const notify = useNotification();
  const dispatch = useDispatch();
  const [createShipmentReceipt, { isLoading: isCreatingShipment }] =
    useConsignmentToReceiptMutation();
  const [createPurchaseReceipt, { isLoading: isCreatingPurchase }] =
    useCreateReceiptsFromPOMutation();
  const isCreating =
    receiptDocument === "purchase" ? isCreatingPurchase : isCreatingShipment;
  const isPurchase = receiptDocument === "purchase";
  const documentSingular = isPurchase ? "purchase order" : "shipment";
  const documentPlural = isPurchase ? "purchase orders" : "shipments";

  const [phase, setPhase] = useState("idle");
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [checkedLineIds, setCheckedLineIds] = useState(() => new Set());
  const [lineQuantities, setLineQuantities] = useState({});
  const [createdReceipts, setCreatedReceipts] = useState([]);
  const [createFailures, setCreateFailures] = useState([]);
  const [linesViewportHeight, setLinesViewportHeight] = useState(0);
  const linesViewportRef = useRef(null);
  const loadContextRef = useRef({
    detailConsignment,
    detailPurchaseOrder,
    preselectedLineIds,
    shipments,
  });

  useEffect(() => {
    if (isOpen) {
      loadContextRef.current = {
        detailConsignment,
        detailPurchaseOrder,
        preselectedLineIds,
        shipments,
      };
    }
  }, [isOpen, detailConsignment, detailPurchaseOrder, preselectedLineIds, shipments]);

  const shippableGroupCount = useMemo(
    () => countGroupsWithLines(previewData?.groups),
    [previewData],
  );
  const singleGroupLayout = shippableGroupCount === 1;

  useEffect(() => {
    if (!singleGroupLayout || phase !== "review") {
      setLinesViewportHeight(0);
      return undefined;
    }

    const element = linesViewportRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const next = Math.max(0, element.clientHeight);
      setLinesViewportHeight(current => (current === next ? current : next));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [phase, singleGroupLayout, previewData]);

  const applyPreview = useCallback(
    (consignments, options = {}) => {
      const preview = buildReceiptPreviewFromConsignments(consignments);
      setPreviewData(preview);
      setCheckedLineIds(
        initialCheckedReceiptLineIds(preview.groups, {
          preselectedLineIds: options.preselectedLineIds,
          selectAllReceivable: options.selectAllReceivable ?? source === "list",
        }),
      );
      setLineQuantities({});
      setCreatedReceipts([]);
      setCreateFailures([]);
      setPhase("review");
    },
    [source],
  );

  const applyPurchasePreview = useCallback((order, options = {}) => {
    const preview = buildReceiptPreviewFromPurchaseOrder(order);
    setPreviewData(preview);
    setCheckedLineIds(
      initialCheckedPurchaseReceiptLineIds(preview.groups, {
        preselectedLineIds: options.preselectedLineIds,
        selectAllReceivable: false,
      }),
    );
    setLineQuantities({});
    setCreatedReceipts([]);
    setCreateFailures([]);
    setPhase("review");
  }, []);

  const loadPreview = useCallback(async () => {
    setPreviewError("");

    const {
      detailConsignment: detail,
      detailPurchaseOrder: purchaseDetail,
      preselectedLineIds: selectedIds,
      shipments: list,
    } = loadContextRef.current;

    try {
      if (source === "detail") {
        if (receiptDocument === "purchase") {
          if (!purchaseDetail?.id) {
            throw new Error("Purchase order is not available.");
          }
          applyPurchasePreview(purchaseDetail, {
            preselectedLineIds: selectedIds,
          });
          return;
        }
        if (!detail?.id) {
          throw new Error("Shipment is not available.");
        }
        applyPreview([detail], {
          preselectedLineIds: selectedIds,
          selectAllReceivable: false,
        });
        return;
      }

      if (!list?.length) {
        throw new Error("Select at least one shipment.");
      }

      setPhase("loading");

      const consignments = await Promise.all(
        list.map(shipment =>
          dispatch(shipmentApiSlice.endpoints.getConsignment.initiate(shipment.id)).unwrap(),
        ),
      );
      applyPreview(consignments, { selectAllReceivable: true });
    } catch (error) {
      const message = getAllocationApiErrorMessage(
        error,
        "Could not load shipment lines for receipt. Please try again.",
      );
      setPreviewError(message);
      setPhase("error");
      notify({ type: "error", message });
    }
  }, [applyPreview, applyPurchasePreview, dispatch, notify, receiptDocument, source]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPhase("idle");
      setPreviewData(null);
      setPreviewError("");
      setCheckedLineIds(new Set());
      setLineQuantities({});
      return;
    }
    if (source === "detail") {
      if (
        receiptDocument === "purchase" &&
        !loadContextRef.current.detailPurchaseOrder?.id
      ) {
        return;
      }
      if (
        receiptDocument === "shipment" &&
        !loadContextRef.current.detailConsignment?.id
      ) {
        return;
      }
    }
    if (source === "list" && !loadContextRef.current.shipments?.length) {
      return;
    }
    loadPreview();
  }, [isOpen, receiptDocument, source, loadPreview]);

  const linesById = useMemo(() => buildLinesById(previewData?.groups), [previewData]);

  const qtyTabOrder = useMemo(() => {
    const order = [];
    (previewData?.groups || []).forEach(group => {
      (group.lines || []).forEach(line => {
        if (checkedLineIds.has(line.line_id)) {
          order.push(line.line_id);
        }
      });
    });
    return order;
  }, [checkedLineIds, previewData]);

  const onQtyChange = (lineId, value) => {
    setLineQuantities(prev => ({ ...prev, [lineId]: value }));
  };

  const toggleLine = lineId => {
    setCheckedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  };

  const toggleGroup = (group, checked) => {
    const ids = (group.lines || []).map(line => line.line_id);
    setCheckedLineIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const hasInvalidCheckedQty = useMemo(() => {
    for (const lineId of checkedLineIds) {
      const line = linesById[lineId];
      if (!line) {
        continue;
      }
      const qtyText = getEditedReceiveQtyText(lineQuantities, lineId, line);
      if (!validateReceiveLineQty(qtyText).valid) {
        return true;
      }
    }
    return false;
  }, [checkedLineIds, lineQuantities, linesById]);

  const summary = useMemo(() => {
    const groups = previewData?.groups || [];
    let shipmentCount = 0;
    let lineCount = 0;
    let totalQty = 0;

    groups.forEach(group => {
      const lines = (group.lines || []).filter(line => checkedLineIds.has(line.line_id));
      if (!lines.length) {
        return;
      }
      shipmentCount += 1;
      lineCount += lines.length;
      lines.forEach(line => {
        totalQty += effectiveReceiveQty(lineQuantities, line.line_id, line);
      });
    });

    return { shipmentCount, lineCount, totalQty };
  }, [checkedLineIds, lineQuantities, previewData]);

  const fullyReceiptedCount = useMemo(
    () => (previewData?.groups || []).filter(group => group.fully_receipted).length,
    [previewData],
  );

  const invalidateAfterReceipt = useCallback(
    documentIds => {
      if (isPurchase) {
        const tags = documentIds.map(id => ({
          type: "purchaseOrderDetail",
          id: Number(id),
        }));
        dispatch(purchaseOrderApiSlice.util.invalidateTags(tags));
        return;
      }
      const tags = ["consignmentList", "filteredConsignmentList"];
      documentIds.forEach(id => {
        tags.push({ type: "consignmentDetail", id: Number(id) });
      });
      dispatch(shipmentApiSlice.util.invalidateTags(tags));
    },
    [dispatch, isPurchase],
  );

  const handleCreate = async () => {
    if (isCreating || hasInvalidCheckedQty || summary.lineCount === 0) {
      return;
    }

    const successes = [];
    const failures = [];

    if (isPurchase) {
      const payload = buildPurchaseOrderToReceiptPayload({
        checkedLineIds,
        lineQuantities,
        linesById,
      });

      if (!payload.items.length) {
        return;
      }

      const group = previewData?.groups?.[0];
      const label =
        group?.reference || group?.shipment_name || `Purchase order ${group?.consignment_id ?? ""}`;

      try {
        const response = await createPurchaseReceipt(payload).unwrap();
        const receipts = Array.isArray(response?.receipts) ? response.receipts : null;
        if (receipts?.length) {
          receipts.forEach((item, index) => {
            const matchedGroup = previewData?.groups?.[index] ?? group;
            const matchedLabel =
              matchedGroup?.reference ||
              matchedGroup?.shipment_name ||
              `Purchase order ${matchedGroup?.consignment_id ?? ""}`;
            successes.push({
              consignmentId: matchedGroup?.consignment_id,
              shipmentName: matchedLabel,
              receiptId: item.receipt_id,
              receiptName: item.receipt_name,
            });
          });
        } else {
          const { receiptId, receiptName } = receiptFromPurchaseOrderResponse(response);
          successes.push({
            consignmentId: group?.consignment_id,
            shipmentName: label,
            receiptId,
            receiptName,
          });
        }
      } catch (error) {
        failures.push({
          name: label,
          message: getAllocationApiErrorMessage(
            error,
            "Could not create receipt for this purchase order.",
          ),
        });
      }
    } else {
      for (const group of previewData?.groups || []) {
        const groupLineIds = new Set((group.lines || []).map(line => line.line_id));
        const checkedInGroup = [...checkedLineIds].filter(id => groupLineIds.has(id));
        if (!checkedInGroup.length) {
          continue;
        }

        const payload = buildConsignmentToReceiptPayload({
          checkedLineIds: checkedInGroup,
          lineQuantities,
          linesById,
        });

        if (!payload.receive_qty.length) {
          continue;
        }

        const label = group.reference || group.shipment_name || `Shipment ${group.consignment_id}`;

        try {
          const response = await createShipmentReceipt(payload).unwrap();
          const { receiptId, receiptName } = receiptFromConsignmentResponse(response);
          successes.push({
            consignmentId: group.consignment_id,
            shipmentName: label,
            receiptId,
            receiptName,
          });
        } catch (error) {
          failures.push({
            name: label,
            message: getAllocationApiErrorMessage(
              error,
              "Could not create receipt for this shipment.",
            ),
          });
        }
      }
    }

    if (failures.length) {
      notify({
        type: "error",
        message: failures.map(item => `${item.name}: ${item.message}`).join(" "),
      });
    }

    if (!successes.length) {
      return;
    }

    invalidateAfterReceipt(successes.map(item => item.consignmentId));
    setCreatedReceipts(successes);
    setCreateFailures(failures);
    setPhase("success");

    notify({
      type: "success",
      message: (
        <>
          Receipt created successfully. Approval required in{" "}
          <Link className="text-white text-decoration-underline" to="/receipts/">
            Receipts
          </Link>{" "}
          page.
        </>
      ),
    });
  };

  const handleDone = () => {
    setSelectedOrder?.([]);
    onSuccess?.();
    onClose();
  };

  const successHeading =
    createdReceipts.length === 1
      ? "Receipt created"
      : `${createdReceipts.length} receipts created`;

  const title = phase === "success" ? successHeading : "Create receipt";

  const canCreate = summary.lineCount > 0 && !isCreating && !hasInvalidCheckedQty;

  const renderShipmentGroups = (fillSingleGroup = false) =>
    previewData?.groups?.map(group => {
      const groupLines = group.lines || [];
      const checkedInGroup = groupLines.filter(line => checkedLineIds.has(line.line_id));
      const groupQty = checkedInGroup.reduce(
        (sum, line) => sum + effectiveReceiveQty(lineQuantities, line.line_id, line),
        0,
      );
      const allChecked =
        groupLines.length > 0 && groupLines.every(line => checkedLineIds.has(line.line_id));
      const someChecked = groupLines.some(line => checkedLineIds.has(line.line_id));
      const groupLabel = group.reference || group.shipment_name || "Shipment";

      if (!groupLines.length) {
        return null;
      }

      const fill = fillSingleGroup && singleGroupLayout;

      return (
        <ShipmentCard key={group.consignment_id} $fill={fill}>
          <ShipmentHeader>
            <span>{groupLabel}</span>
            <span>
              {checkedInGroup.length} / {groupLines.length} lines · {groupQty} qty
            </span>
          </ShipmentHeader>
          {fill ? (
            <LinesViewport ref={linesViewportRef}>
              <PreviewLines
                allChecked={allChecked}
                checkedLineIds={checkedLineIds}
                lineQuantities={lineQuantities}
                lines={groupLines}
                onQtyChange={onQtyChange}
                qtyTabOrder={qtyTabOrder}
                someChecked={someChecked}
                toggleGroup={checked => toggleGroup(group, checked)}
                toggleLine={toggleLine}
                viewportHeight={linesViewportHeight}
                groupLabel={groupLabel}
              />
            </LinesViewport>
          ) : (
            <PreviewLines
              allChecked={allChecked}
              checkedLineIds={checkedLineIds}
              lineQuantities={lineQuantities}
              lines={groupLines}
              onQtyChange={onQtyChange}
              qtyTabOrder={qtyTabOrder}
              someChecked={someChecked}
              toggleGroup={checked => toggleGroup(group, checked)}
              toggleLine={toggleLine}
              groupLabel={groupLabel}
            />
          )}
        </ShipmentCard>
      );
    });

  return (
    <>
      <DrawerTheme />
      <Drawer
        open={isOpen}
        title={<PortalHeader title={title} onClose={onClose} />}
        rootClassName="shipment-create-receipt-drawer"
        placement="right"
        width="min(1240px, calc(100vw - 32px))"
        styles={{
          header: { padding: 0 },
          body: { padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(1px)" },
        }}
        onClose={onClose}
        closable={false}
        maskClosable={false}
        keyboard
        destroyOnClose
      >
        <Body>
          {phase === "loading" && (
            <Scroll>
              <Skeleton active paragraph={{ rows: 8 }} />
            </Scroll>
          )}

          {phase === "error" && (
            <ErrorBlock>
              <p>{previewError}</p>
              <Button btnName="Retry" handler={loadPreview} classOfBtn="submitBtn" />
            </ErrorBlock>
          )}

          {phase === "review" && previewData && (
            <>
              <SummaryBar>
                {summary.shipmentCount === 0 ? (
                  <span>No lines selected. Check at least one line to create a receipt.</span>
                ) : (
                  <span>
                    <strong>{summary.shipmentCount}</strong>{" "}
                    {summary.shipmentCount === 1 ? documentSingular : documentPlural} ·{" "}
                    <strong>{summary.lineCount}</strong>{" "}
                    {summary.lineCount === 1 ? "line" : "lines"} ·{" "}
                    <strong>{summary.totalQty}</strong> total qty
                  </span>
                )}
              </SummaryBar>
              {fullyReceiptedCount > 0 && (
                <FullyReceiptedNote role="note">
                  {fullyReceiptedCount === 1
                    ? `This ${documentSingular} has been receipted in full already.`
                    : `These ${documentPlural} have been receipted in full already.`}
                </FullyReceiptedNote>
              )}

              {singleGroupLayout ? (
                <ReviewMain>
                  {!previewData.groups?.some(g => g.lines?.length) &&
                    !previewData.skipped_lines?.length && (
                      <p>
                        No receivable lines were found on the selected{" "}
                        {summary.shipmentCount === 1 ? documentSingular : documentPlural}.
                      </p>
                    )}
                  {renderShipmentGroups(true)}
                  {previewData.skipped_lines?.length > 0 && (
                    <SkippedSection>
                      <h4>Not included</h4>
                      <ul>
                        {previewData.skipped_lines.map(line => (
                          <li key={line.line_id}>
                            {line.order_number} ({line.product_number}) — {line.reason}
                          </li>
                        ))}
                      </ul>
                    </SkippedSection>
                  )}
                </ReviewMain>
              ) : (
                <Scroll>
                  {!previewData.groups?.some(g => g.lines?.length) &&
                    !previewData.skipped_lines?.length && (
                      <p>
                        No receivable lines were found on the selected{" "}
                        {summary.shipmentCount === 1 ? documentSingular : documentPlural}.
                      </p>
                    )}
                  {renderShipmentGroups(false)}
                  {previewData.skipped_lines?.length > 0 && (
                    <SkippedSection>
                      <h4>Not included</h4>
                      <ul>
                        {previewData.skipped_lines.map(line => (
                          <li key={line.line_id}>
                            {line.order_number} ({line.product_number}) — {line.reason}
                          </li>
                        ))}
                      </ul>
                    </SkippedSection>
                  )}
                </Scroll>
              )}

              <Footer>
                <Button btnName="Cancel" handler={onClose} classOfBtn="cancelBtn" />
                <Button
                  btnName={isCreating ? "Creating…" : "Create receipt"}
                  handler={handleCreate}
                  classOfBtn="submitBtn"
                  isDisabledButton={!canCreate || isCreating}
                />
              </Footer>
            </>
          )}

          {phase === "success" && (
            <>
              <Scroll>
                <SuccessColumn>
                  <SuccessContentCenter>
                    <SuccessHero>
                      <SuccessIconWrap aria-hidden="true">
                        <CheckCircleFilled />
                      </SuccessIconWrap>
                      <SuccessHeading>{successHeading}</SuccessHeading>
                      <SuccessSubtext>
                        {createdReceipts.length === 1
                          ? "Approve it on the "
                          : "Approve them on the "}
                        <Link to="/receipts/">Receipts</Link> page before use.
                      </SuccessSubtext>
                    </SuccessHero>

                    {createFailures.length > 0 && (
                      <FailureCallout role="alert">
                        <h3>
                          Some {documentPlural} could not be receipted
                        </h3>
                        <ul>
                          {createFailures.map(item => (
                            <li key={item.name}>
                              <strong>{item.name}</strong>: {item.message}
                            </li>
                          ))}
                        </ul>
                      </FailureCallout>
                    )}

                    <SuccessTableWrap>
                      <SuccessTable>
                        <thead>
                          <tr>
                            <th scope="col">{isPurchase ? "Purchase order" : "Shipment"}</th>
                            <th scope="col">Receipt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {createdReceipts.map(item => {
                            const receiptNumber = formatReceiptNumber(
                              item.receiptName,
                              item.receiptId,
                            );
                            const receiptHref = item.receiptId
                              ? `/receipts/${item.receiptId}`
                              : "/receipts/";
                            return (
                              <tr key={`${item.consignmentId}-${item.receiptId ?? "receipt"}`}>
                                <td>
                                  <Link
                                    to={
                                      isPurchase
                                        ? `/orders/purchase/${item.consignmentId}`
                                        : `/shipment/po-to/${item.consignmentId}`
                                    }
                                  >
                                    {item.shipmentName}
                                  </Link>
                                </td>
                                <td>
                                  {receiptNumber ? (
                                    <Link to={receiptHref}>{receiptNumber}</Link>
                                  ) : (
                                    <Link to={receiptHref}>Open receipt</Link>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </SuccessTable>
                    </SuccessTableWrap>
                  </SuccessContentCenter>
                </SuccessColumn>
              </Scroll>
              <Footer>
                <Button btnName="Done" handler={handleDone} classOfBtn="submitBtn" />
              </Footer>
            </>
          )}

          {isCreating && phase === "review" && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin size="large" />
            </div>
          )}
        </Body>
      </Drawer>
    </>
  );
};
